var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Box3, BufferGeometry, Cache, Group, Matrix4, Mesh, MeshPhongMaterial, Vector3 } from "three";
import { ThreeMFLoader } from "three/examples/jsm/loaders/3MFLoader.js";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { CustomOBJLoader } from "../loaders/CustomOBJLoader";
import { renderer } from "../RendererGlobals";
const tmpBox3 = new Box3();
class MeshLoaderService {
    constructor() {
        this.meshPromises = new Map();
        this.objLoader = new OBJLoader();
        this.gltfLoader = new GLTFLoader();
        this.fbxLoader = new FBXLoader();
        this.stlLoader = new STLLoader();
        this.threeMfLoader = new ThreeMFLoader();
        this.plyLoader = new PLYLoader();
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath("/lib/draco/");
        this.gltfLoader.setDRACOLoader(dracoLoader);
        const ktx2Loader = new KTX2Loader().detectSupport(renderer);
        this.gltfLoader.setKTX2Loader(ktx2Loader);
        this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);
    }
    clearCache() {
        Cache.clear();
        this.meshPromises.clear();
    }
    load(url, fileExtension) {
        return __awaiter(this, void 0, void 0, function* () {
            const existing = this.meshPromises.get(url);
            if (existing) {
                return yield existing;
            }
            if (!fileExtension) {
                const dotIndex = url.lastIndexOf(".");
                fileExtension = dotIndex !== -1 ? url.substring(dotIndex + 1) : "";
            }
            else if (fileExtension.startsWith(".")) {
                fileExtension = fileExtension.substring(1);
            }
            const meshPromise = this.loadMesh(url, fileExtension).catch((error) => {
                this.meshPromises.delete(url);
                throw error;
            });
            this.meshPromises.set(url, meshPromise);
            return yield meshPromise;
        });
    }
    loadMesh(url, fileExtension) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (fileExtension.toLowerCase()) {
                case "glb":
                case "gltf":
                    return yield this.loadGLTF(url).then((gltf) => {
                        const group = new Group();
                        group.matrixAutoUpdate = false;
                        group.add(...gltf.scene.children);
                        return group;
                    });
                case "fbx":
                    return yield this.loadFBX(url);
                case "obj":
                    return yield this.loadOBJ(url);
                case "stl":
                    return yield this.loadSTL(url);
                case "3mf":
                    return yield this.load3MF(url);
                case "ply":
                    return yield this.loadPLY(url);
                default:
                    return yield new CustomOBJLoader().loadMtlAndObjPromise(url);
            }
        });
    }
    loadOBJ(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.objLoader.loadAsync(url).then((group) => {
                group.traverse((child) => {
                    child.matrixAutoUpdate = false;
                    child.updateMatrix();
                });
                return group;
            });
        });
    }
    loadGLTF(url) {
        return __awaiter(this, void 0, void 0, function* () {
            if (url.startsWith("@")) {
                url = this.resolveModelUrl(url);
            }
            url = yield this.resolveLayouterModelUrl(url);
            return yield this.gltfLoader.loadAsync(url).then((gltf) => {
                gltf.scene.traverse((child) => {
                    child.matrixAutoUpdate = false;
                    child.updateMatrix();
                });
                return gltf;
            });
        });
    }
    loadFBX(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.fbxLoader.loadAsync(url).then((group) => {
                group.traverse((child) => {
                    child.matrixAutoUpdate = false;
                    child.updateMatrix();
                });
                return group;
            });
        });
    }
    loadSTL(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.stlLoader.loadAsync(url).then((bufferGeometry) => {
                const material = new MeshPhongMaterial();
                const mesh = new Mesh(bufferGeometry, material);
                mesh.matrixAutoUpdate = false;
                mesh.updateMatrix();
                mesh.geometry.computeBoundingBox();
                return mesh;
            });
        });
    }
    load3MF(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.threeMfLoader.loadAsync(url).then((group) => {
                group.traverse((child) => {
                    child.matrixAutoUpdate = false;
                    child.updateMatrix();
                });
                return group;
            });
        });
    }
    loadPLY(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.plyLoader.loadAsync(url).then((bufferGeometry) => {
                const material = new MeshPhongMaterial();
                const mesh = new Mesh(bufferGeometry, material);
                mesh.matrixAutoUpdate = false;
                mesh.updateMatrix();
                mesh.geometry.computeBoundingBox();
                return mesh;
            });
        });
    }
    resolveModelUrl(fileId) {
        const pos = fileId.lastIndexOf(".");
        fileId = fileId.substring(1, pos);
        return `/api/v1/asset/${fileId}/model.*`;
    }
    resolveLayouterModelUrl(url) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            if (!url.startsWith("/api/v1/asset/") || !url.endsWith("/model.*")) {
                return url;
            }
            let probeResponse;
            try {
                probeResponse = yield fetch(url, {
                    method: "GET",
                    headers: {
                        Range: "bytes=0-4095"
                    }
                });
            }
            catch (_d) {
                return url;
            }
            if (probeResponse.status === 401) {
                throw new Error(`[mesh] Unauthorized while loading model: ${url}`);
            }
            if (!probeResponse.ok) {
                throw new Error(`[mesh] Failed to load model ${url}: HTTP ${probeResponse.status}`);
            }
            const contentType = (_a = probeResponse.headers.get("content-type")) !== null && _a !== void 0 ? _a : "";
            if (!contentType.includes("json") && !contentType.includes("text")) {
                return url;
            }
            const payload = yield probeResponse.text();
            if (!payload.trimStart().startsWith("{")) {
                return url;
            }
            let json;
            try {
                json = JSON.parse(payload);
            }
            catch (_e) {
                return url;
            }
            if (typeof (json === null || json === void 0 ? void 0 : json.msg) === "string" && json.msg.length > 0) {
                throw new Error(`[mesh] Failed to load model ${url}: ${json.msg}`);
            }
            const layouterModelFileId = (_b = json === null || json === void 0 ? void 0 : json.layouterModel) === null || _b === void 0 ? void 0 : _b.fileId;
            if (typeof layouterModelFileId !== "string" || layouterModelFileId.length === 0) {
                return url;
            }
            const layouterModelExt = typeof ((_c = json === null || json === void 0 ? void 0 : json.layouterModel) === null || _c === void 0 ? void 0 : _c.ext) === "string" && json.layouterModel.ext.length > 0 ? json.layouterModel.ext : ".*";
            return `/api/v1/asset/${layouterModelFileId}/model${layouterModelExt}`;
        });
    }
    findPieceObject(obj, pieceName) {
        if (this.isPieceNameAPath(pieceName)) {
            const path = pieceName.split("/");
            path.shift();
            const piece = this.getObjectByPath(obj, path);
            if (piece)
                return piece;
        }
        const piece = obj.getObjectByName(pieceName);
        if (piece === undefined)
            throw new Error(`Couldn't find mesh piece with name ${pieceName}`);
        return piece;
    }
    verticesFromMesh(mesh, traverse) {
        const vertices = [];
        const colors = [];
        const matrices = [];
        const indexes = [];
        mesh.updateMatrixWorld(true);
        if (traverse) {
            this.gatherGeometries(mesh, vertices, colors, indexes, matrices);
        }
        else if (mesh instanceof Mesh) {
            const transform = new Matrix4();
            this.getGeometry(mesh, vertices, colors, indexes, matrices, transform);
        }
        else {
            // Blender exports a Group instead of a single Mesh when a piece has multiple materials (one child Mesh per
            // material) - those children are still logically one collision piece, not separate compound elements (which
            // are genuinely separate models elsewhere in the scene graph), so merge them together. gatherGeometries is
            // safe to use here even though it's normally reserved for the "traverse the whole asset" case, since
            // findPieceObject already scoped `mesh` down to just this one piece's own subtree.
            this.gatherGeometries(mesh, vertices, colors, indexes, matrices);
            if (vertices.length === 0) {
                console.warn(`[mesh] Collision piece "${mesh.name}" has no geometry (not a Mesh, and no Mesh descendants) - collider will be empty`);
            }
        }
        return {
            vertices,
            colors,
            indexes,
            matrices
        };
    }
    computeGroupBounding(root, aabb, boundingSphere) {
        aabb.makeEmpty();
        boundingSphere.makeEmpty();
        const objectMatrixMap = new Map();
        objectMatrixMap.set(root.uuid, new Matrix4());
        root.traverse((node) => {
            var _a, _b, _c;
            if (node.geometry !== undefined) {
                const geometry = node.geometry;
                if (geometry.boundingBox === null) {
                    geometry.computeBoundingBox();
                }
                if (geometry.boundingBox !== null) {
                    const parentUUID = (_b = (_a = node.parent) === null || _a === void 0 ? void 0 : _a.uuid) !== null && _b !== void 0 ? _b : "unknown";
                    const parentMatrix = (_c = objectMatrixMap.get(parentUUID)) !== null && _c !== void 0 ? _c : new Matrix4();
                    const nodeMatrix = (node === root) ? new Matrix4() : new Matrix4().multiplyMatrices(parentMatrix, node.matrix);
                    aabb.union(tmpBox3.copy(geometry.boundingBox).applyMatrix4(nodeMatrix));
                    objectMatrixMap.set(parentUUID, parentMatrix);
                }
            }
        });
        aabb.getBoundingSphere(boundingSphere);
        return {
            aabb,
            boundingSphere
        };
    }
    getGeometry(mesh, vertices, colors, indices, matrices, transform) {
        if (!(mesh.geometry instanceof BufferGeometry)) {
            return;
        }
        const index = mesh.geometry.index;
        const position = mesh.geometry.attributes.position;
        const color = mesh.geometry.attributes.color;
        vertices.push(position.array);
        indices.push(index !== null ? index.array : []);
        matrices.push(transform.elements);
        if (!color) {
            const colorsForEachVertex = [];
            for (let numVertexAttribute = 0; numVertexAttribute < position.array.length; numVertexAttribute += 1) {
                colorsForEachVertex.push(1);
            }
            colors.push(colorsForEachVertex);
        }
        else {
            const colorsForEachVertex = [];
            for (let numVertexAttribute = 0; numVertexAttribute < position.array.length / 3; numVertexAttribute += 1) {
                colorsForEachVertex.push(color.array[numVertexAttribute * 4] / 65535);
                colorsForEachVertex.push(color.array[numVertexAttribute * 4 + 1] / 65535);
                colorsForEachVertex.push(color.array[numVertexAttribute * 4 + 2] / 65535);
            }
            colors.push(colorsForEachVertex);
        }
    }
    gatherGeometries(root, vertices, colors, indexes, matrices) {
        const inverse = new Matrix4();
        inverse.copy(root.matrixWorld).invert();
        const scale = new Vector3();
        scale.setFromMatrixScale(root.matrixWorld);
        root.traverse((mesh) => {
            const transform = new Matrix4();
            if (mesh instanceof Mesh && mesh.visible) {
                if (mesh === root) {
                    transform.identity();
                }
                else {
                    mesh.updateWorldMatrix(true, false);
                    transform.multiplyMatrices(inverse, mesh.matrixWorld);
                }
                this.getGeometry(mesh, vertices, colors, indexes, matrices, transform);
            }
        });
    }
    isKitObject(obj) {
        const objComp = this.getKitComponent(obj, "kit-piece");
        if (objComp !== undefined) {
            return true;
        }
        // look at first child
        if (obj.children.length === 0)
            return false;
        const childComp = this.getKitComponent(obj.children[0], "kit-piece");
        return !!childComp;
    }
    getKitComponent(obj, componentName) {
        const extensions = obj.userData.gltfExtensions;
        if (extensions === undefined)
            return undefined;
        if (extensions.MOZ_hubs_components === undefined)
            return undefined;
        return extensions.MOZ_hubs_components[componentName];
    }
    isPieceNameAPath(name) {
        return name.startsWith("/");
    }
    getObjectByPath(obj, path) {
        const objectName = path.shift();
        if (objectName === undefined) {
            return obj;
        }
        if (path.length === 0 && objectName.endsWith("$")) {
            const idx = objectName.lastIndexOf("_");
            const str = objectName.substring(idx + 1, objectName.length - 1);
            const childIdx = Number(str);
            const child = obj.children[childIdx];
            if (child)
                return child;
            else
                console.warn("No child was found!");
        }
        for (const child of obj.children) {
            if (child.name === objectName) {
                return this.getObjectByPath(child, path);
            }
        }
        // check if added ('_' + i) because createUniqueName
        const regex = new RegExp(`^${objectName}_\\d+`);
        for (const child of obj.children) {
            if (regex.test(child.name)) {
                return this.getObjectByPath(child, path);
            }
        }
        console.warn(`Cant find piece mesh ${objectName}`);
        return undefined;
    }
}
export const meshLoaderService = new MeshLoaderService();
